import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _get from "lodash/get";
var _excluded = ["active", "activeBg", "alignSelf", "ariaLabel", "ariaExpanded", "ariaHaspopup", "onSpaceOrEnter", "border", "minHeight", "minWidth", "maxWidth", "borderRadius", "className", "color", "icon", "rightIcon", "large", "loading", "small", "justifyContent", "iconSize", "width", "height", "textAlign", "textColor", "title", "bg", "flexDirection", "alignItems", "flexWrap", "tracker", "shouldDismissPopover", "staticContext"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { display, fontFamily, fontSize, fontWeight, space, themeGet } from 'styled-system';
import { Button as BlueprintButton, Classes } from '@blueprintjs/core';
import classNames from 'classnames';
import { formAriaProps, guessByChildText, guessTextOnIcon } from 'core/util/ariaUtils';
import { omitAriaProps } from 'core/form/components/util/omitFieldProps';
import Icon from './Icon';
import position from './utils/position';
import flex from './utils/flex';
import size from './utils/size';
var StyledButton = styled(BlueprintButton).withConfig({
  displayName: "Button__StyledButton",
  componentId: "sc-177yb33-0"
})(["&&{&.", "{", ";", ";", ";", ";", ";", ";", ";", ";", " > .", "{overflow:hidden;flex:1 1 auto;text-align:left;color:", ";}&.align-center{> .", "{text-align:center;}}", "}}"], Classes.BUTTON, display, fontFamily, fontSize, fontWeight, space, position, flex, size, function (_ref) {
  var theme = _ref.theme,
    small = _ref.small;
  return small && css(["font-size:", ";.bp4-icon{&:first-child{margin-right:4px;}&:first-child:last-child{margin:0 -7px;}}"], theme === null || theme === void 0 ? void 0 : theme.fontSizes.small);
}, Classes.BUTTON_TEXT, function (_ref2) {
  var color = _ref2.color;
  return themeGet("colors.".concat(color), color);
}, Classes.BUTTON_TEXT, function (props) {
  return props.ellipsis && css(["> .", "{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}"], Classes.BUTTON_TEXT);
});
var Button = /*#__PURE__*/React.forwardRef(function (props, ref) {
  // destructure minWidth and alignSelf, otherwise we get non-standard prop warnings from React.
  var active = props.active,
    activeBg = props.activeBg,
    alignSelf = props.alignSelf,
    ariaLabel = props.ariaLabel,
    ariaExpanded = props.ariaExpanded,
    ariaHaspopup = props.ariaHaspopup,
    onSpaceOrEnter = props.onSpaceOrEnter,
    border = props.border,
    minHeight = props.minHeight,
    minWidth = props.minWidth,
    maxWidth = props.maxWidth,
    borderRadius = props.borderRadius,
    className = props.className,
    color = props.color,
    icon = props.icon,
    rightIcon = props.rightIcon,
    large = props.large,
    loading = props.loading,
    small = props.small,
    justifyContent = props.justifyContent,
    iconSize = props.iconSize,
    width = props.width,
    height = props.height,
    textAlign = props.textAlign,
    textColor = props.textColor,
    title = props.title,
    bg = props.bg,
    flexDirection = props.flexDirection,
    alignItems = props.alignItems,
    flexWrap = props.flexWrap,
    tracker = props.tracker,
    _shouldDismissPopover = props.shouldDismissPopover,
    _staticContext = props.staticContext,
    rest = _objectWithoutProperties(props, _excluded);
  var theme = useTheme();
  var finalIconSize = 16;
  if (iconSize) {
    finalIconSize = iconSize;
  } else if (large) {
    finalIconSize = 20;
  } else if (small) {
    finalIconSize = 12;
  }
  var background = bg;
  if (active && activeBg) {
    background = activeBg;
  }
  var ariaLabelGuess = rest.ellipsis ? rest.text : title;
  ariaLabelGuess = ariaLabelGuess || rest.text || guessByChildText(rest.children) || icon && guessTextOnIcon(icon) || rightIcon && guessTextOnIcon(rightIcon);
  var ariaLabelText = ariaLabel || ariaLabelGuess;
  var buttonClassName = classNames(className, tracker, {
    'align-center': textAlign === 'center'
  });
  var ariaProps = formAriaProps(props, {
    ariaLabel: ariaLabelText || rest.text,
    ariaPressed: active,
    ariaSelected: rest.ariaSelected || active,
    name: rest.name || rest.text || ariaLabel || rest['aria-label'],
    ariaHaspopup: ariaHaspopup,
    ariaExpanded: ariaExpanded
  }, {
    enableTabIndexNotDisabled: true,
    explicitTabIndex: rest.explicitTabIndex || 0
  });
  if (rest.role) {
    // @ts-expect-error need to fix up the formAriaProps function
    ariaProps.role = rest.role;
  } else {
    // @ts-expect-error need to fix up the formAriaProps function
    ariaProps.role = 'button';
  }
  if (onSpaceOrEnter) {
    // @ts-expect-error this works but isn't really legal lol
    ariaProps.onKeyDown = function (event) {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        onSpaceOrEnter(event);
      }
    };
  }
  return /*#__PURE__*/React.createElement(StyledButton
  // rest has a tabIndex=undefined so it has to go first
  , _extends({}, omitAriaProps(rest), ariaProps, {
    active: active,
    large: large,
    loading: loading,
    small: small,
    className: buttonClassName,
    style: _objectSpread({
      alignSelf: alignSelf,
      alignItems: alignItems,
      flexWrap: flexWrap,
      border: border,
      minHeight: minHeight,
      minWidth: minWidth,
      maxWidth: maxWidth,
      borderRadius: borderRadius,
      justifyContent: justifyContent,
      flexDirection: flexDirection,
      width: width,
      height: height,
      backgroundColor: background ? _get(theme === null || theme === void 0 ? void 0 : theme.colors, background) : undefined,
      color: textColor && _get(theme === null || theme === void 0 ? void 0 : theme.colors, textColor) || color && _get(theme === null || theme === void 0 ? void 0 : theme.colors, color) || undefined
    }, rest.style || {}),
    title: rest.ellipsis ? rest.text : title,
    icon: icon ? /*#__PURE__*/React.createElement(Icon, {
      icon: icon,
      color: color,
      disabled: rest === null || rest === void 0 ? void 0 : rest.disabled,
      iconSize: finalIconSize
    }) : undefined,
    rightIcon: rightIcon ? /*#__PURE__*/React.createElement(Icon, {
      icon: rightIcon,
      color: color,
      iconSize: finalIconSize
    }) : undefined,
    ref: ref
  }));
});
Button.defaultProps = {
  fontFamily: 'body',
  textAlign: 'center',
  ariaHaspopup: false
};
Button.displayName = 'Button';
export default Button;